import React, { useEffect } from 'react';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useSocialLoginUrls from '@jwp/ott-hooks-react/src/useSocialLoginUrls';
import type { LoginFormData } from '@jwp/ott-common/types/account';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import ReactGA from 'react-ga4';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import LoginForm from '../../../components/LoginForm/LoginForm';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

type Props = {
  messageKey: string | null;
};

const Login: React.FC<Props> = ({ messageKey }: Props) => {
  const accountController = getModule(AccountController);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { siteName } = useConfigStore((s) => s.config);
  const recaptchaAPI = useConfigStore((s) => s.appConfig.lambda.recaptcha_verification);
  const navigate = useNavigate();
  const { isGoogleAnalytics, authentication_type, isGoogleCaptchaEnabled } = useAppConfig();
  const deviceType = true ? useGADeviceType() : null;
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  const qr = useQueryParam('qr');

  const socialLoginURLs = useSocialLoginUrls(window.location.href.split('?')[0]);

  const { values, errors, submitting, validationSchemaError, handleSubmit, handleChange } = useForm<LoginFormData>({
    initialValues: { email: '', password: '' },
    validationSchema: object().shape({
      email: string()
        .email(t('login.field_is_not_valid_email'))
        .required(t('login.field_required', { field: t('login.email') })),
      password: string().required(t('login.field_required', { field: t('login.password') })),
    }),
    onSubmit: async ({ email, password }) => {
      try {
        if (isGoogleCaptchaEnabled) {
          if (!executeRecaptcha) {
            console.warn('reCAPTCHA not ready');
            throw new Error('reCAPTCHA not ready');
          }

          const token = await executeRecaptcha('login');
          const verifyResponse = await fetch(recaptchaAPI, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });

          const { success, score, action } = await verifyResponse.json();

          if (!success || score < 0.5 || action !== 'login') {
            throw new Error('reCAPTCHA validation failed');
          }
        }

        return accountController.login(email, password, window.location.href, t);
      } catch (error) {
        console.error('Login or reCAPTCHA failed:', error);
        throw error;
      }
    },
    onSubmitSuccess: (values, response) => {
      sendGAEvent('login_success', {
        provider: authentication_type,
        method: authentication_type,
        userid_no: response?.user?.id,
        device_type: deviceType,
      });

      announce(t('login.sign_in_success'), 'success');

      !qr && navigate('/');
    },
    onSubmitError: ({ resetValue, error }) => {
      let errorMessage = 'Unknown error';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      sendGAEvent('login_failure', {
        email: values.email,
        error_message: errorMessage,
        device_type: deviceType,
      });

      resetValue('password');
    },
  });

  useEffect(() => {
    let hasValidToken = false;

    const authRaw = localStorage.getItem('jwapp.auth');

    if (authRaw) {
      try {
        const auth = JSON.parse(authRaw);

        if (auth?.accessToken && auth?.refreshToken) {
          hasValidToken = true;
        }
      } catch (e) {
        console.error('Invalid auth JSON');
      }
    }

    // Old legacy tokens (same check as before)
    if (localStorage.getItem('access_token')) { hasValidToken = true; }
    if (localStorage.getItem('inplayer_token')) { hasValidToken = true; }

    if (hasValidToken) {
      if (qr) {
        navigate('/activate');
        return;
      }
      navigate('/');
    }
  }, []);

  return (
    <LoginForm
      values={values}
      validationError={validationSchemaError}
      errors={errors}
      submitting={submitting}
      siteName={siteName}
      socialLoginURLs={socialLoginURLs}
      messageKey={messageKey}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
};

export default Login;
